import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { fetchAdmins, adminSelector, removeMultipleAdmins } from "slices/adminSlice";
import ConfirmationModal from "components/common/ConfirmationMessage";
import { ListLayout } from "components/common/ListLayout";
import TableHeaderItem from "components/common/ListLayout/TableHeaderItem";
import { usePermissions } from "layouts/Admin";
import moment from "moment";

// Defensive: never display this account even if the API ever returns it
const ALWAYS_HIDDEN = ["admin@algovicrm.com"];

function AdminsList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { checkPermission } = usePermissions();
  const { admin: adminsRaw, last_page: totalPages, loader, total, current_page, per_page: pageLength } = useSelector(adminSelector);

  const admins = (adminsRaw || []).filter((a) => !ALWAYS_HIDDEN.includes(a?.email));

  const refAddutton = React.useRef(null);
  const [selectedItems, setSelectedItems] = React.useState([]);
  const [selectAll, setSelectAll] = React.useState(false);
  const [deleteModal, setDeleteModal] = React.useState(false);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [sortConfig, setSortConfig] = React.useState({
    sortBy: "name",
    sortDirection: "asc",
  });

  const ADMIN_PAGE_LENGTH = 100;

  React.useEffect(() => {
    dispatch(fetchAdmins(1, { pageLength: ADMIN_PAGE_LENGTH, sortBy: "name", sortDirection: "asc" }));
  }, []);

  const submitCallback = (status, message) => {
    if (status) {
      toast.success(message, { className: "successToast" });
      setSelectedItems([]);
      setSelectAll(false);
      dispatch(fetchAdmins());
      hideConfirmDialog();
    } else {
      toast.error(message, { className: "errorToast" });
    }
  };

  const showConfirmDialog = () => setDeleteModal(true);
  const hideConfirmDialog = () => setDeleteModal(false);

  const handleRemoveItems = () => {
    dispatch(removeMultipleAdmins({ ids: selectedItems }, submitCallback));
  };

  const handleChange = (e) => {
    const id = e.target.dataset.value;
    setSelectedItems(
      selectedItems.includes(id)
        ? selectedItems.filter((item) => item != id)
        : [...selectedItems, id]
    );
  };

  // Only non-protected admins are selectable for deletion
  const deletableAdmins = () => admins.filter((a) => !a?.is_protected);

  const handleToggleSelectAll = () => {
    if (selectAll) {
      setSelectedItems([]);
      setSelectAll(false);
      return;
    }
    setSelectAll(true);
    setSelectedItems(deletableAdmins().map((a) => a.id.toString()));
  };

  //#region TABLE HEAD
  const renderTableHeader = React.useCallback(() => {
    const callBack = (dir, sortBy) => {
      setSortConfig({ sortBy, sortDirection: dir });
      dispatch(fetchAdmins(currentPage, { sortBy, sortDirection: dir, search: searchTerm, pageLength: ADMIN_PAGE_LENGTH }));
    };
    return (
      <tr>
        <th className="col-1">
          <div className="row_check">
            <span>#</span>
            <label className="custom-checkbox checkbox">
              <input type="checkbox" name="select-all" checked={selectAll} onChange={handleToggleSelectAll} />
              <span className="checkmark"></span>
            </label>
          </div>
        </th>
        <th>
          <TableHeaderItem title="Admin Name" sortBy="name" sortDirection={sortConfig.sortDirection} callBack={callBack} />
        </th>
        <th>
          <TableHeaderItem title="Email" sortBy="email" sortDirection={sortConfig.sortDirection} callBack={callBack} />
        </th>
        <th>
          <TableHeaderItem title="Creation Date" sortBy="created_at" sortDirection={sortConfig.sortDirection} callBack={callBack} />
        </th>
        <th className="col-1">Actions</th>
      </tr>
    );
  }, [selectAll, searchTerm, sortConfig, currentPage, selectedItems, admins]);
  //#endregion

  //#region TABLE BODY
  const renderTableContent = React.useCallback(() => {
    return admins.map((admin, index) => (
      <AdminItem
        key={admin.id}
        admin={admin}
        index={index}
        selectedItems={selectedItems}
        handleChange={handleChange}
        current_page={current_page}
        pageLength={pageLength}
      />
    ));
  }, [admins, selectedItems, current_page]);
  //#endregion

  return (
    <ListLayout
      heading="Admins"
      time={true}
      title=""
      renderTableHeader={renderTableHeader}
      renderTableContent={renderTableContent}
      totalPages={totalPages}
      fetchList={fetchAdmins}
      selectedItems={selectedItems}
      addButtonDisabled={true}
      addformModal={false}
      handleRemoveItems={handleRemoveItems}
      renderAddLink={() => <Link to="/admins/create" className="main_button ml-4">Add Admin</Link>}
      refAddutton={refAddutton}
      showConfirmDialog={showConfirmDialog}
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      loader={loader}
      searchPlaceholder="Search Admins ..."
      noOfItems={total}
      disableBootstrap={true}
      defaultPageLength={ADMIN_PAGE_LENGTH}
      paginationParams={{ pageLength: ADMIN_PAGE_LENGTH }}
    >
      {deleteModal && <ConfirmationModal onClose={hideConfirmDialog} isOpen={deleteModal} onConfirm={handleRemoveItems} />}
    </ListLayout>
  );
}

const AdminItem = ({ admin, index, selectedItems, handleChange, current_page, pageLength }) => {
  const serial = useMemo(() => {
    return index + 1 + (((current_page || 1) - 1) * (pageLength || 10));
  }, [current_page, pageLength, index]);

  return (
    <tr>
      <td>
        <div className="row_check">
          <span>{serial}</span>
          {/* Protected admins (e.g. admin@kingcounty.org) cannot be selected/deleted */}
          {!admin?.is_protected && (
            <label className="custom-checkbox checkbox">
              <input
                type="checkbox"
                name="select-all"
                data-value={admin?.id}
                checked={selectedItems.includes(admin?.id?.toString())}
                onChange={handleChange}
              />
              <span className="checkmark"></span>
            </label>
          )}
        </div>
      </td>
      <td>{admin?.name}</td>
      <td>{admin?.email}</td>
      <td>{admin?.created_at ? moment(admin.created_at).format("YYYY-MM-DD h:mm A") : "-"}</td>
      <td>
        {/* Protected admins (e.g. admin@kingcounty.com) cannot be edited */}
        {!admin?.is_protected && (
          <div className="row_check">
            <Link to={`/admins/edit/${admin?.id}`} className="table_edit">Edit</Link>
          </div>
        )}
      </td>
    </tr>
  );
};

export default AdminsList;
